import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Grid, Avatar, Divider, Alert,
  Chip, Stack, Tab, Tabs, Autocomplete,
} from '@mui/material';
import { Save, CheckCircle, Warning, Upload, VerifiedUser, CloudUpload, CameraAlt } from '@mui/icons-material';
import useAuthStore from '../../store/useStore';
import { authAPI, kycAPI, ghanaAPI } from '../../api/client';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join('; ');
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name: user?.first_name || '', last_name: user?.last_name || '',
    phone: user?.phone || '', university: user?.university || '',
    student_id: user?.student_id || '', course: user?.course || '',
    year_of_study: user?.year_of_study || '', department: user?.department || '',
    level: user?.level || '', enrollment_year: user?.enrollment_year || '',
    expected_graduation: user?.expected_graduation || '',
    company_name: user?.company_name || '',
    bio: user?.bio || '', address_line1: user?.address_line1 || '',
    city: user?.city || '', region: user?.region || '', postal_code: user?.postal_code || '',
    date_of_birth: user?.date_of_birth || '', nationality: user?.nationality || '',
    gender: user?.gender || '',
    guardian_name: user?.guardian_name || '', guardian_phone: user?.guardian_phone || '',
    guardian_email: user?.guardian_email || '', guardian_relationship: user?.guardian_relationship || '',
    guardian_address: user?.guardian_address || '',
    emergency_contact_name: user?.emergency_contact_name || '',
    emergency_contact_phone: user?.emergency_contact_phone || '',
    emergency_contact_relationship: user?.emergency_contact_relationship || '',
  });
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycForm, setKycForm] = useState({ id_type: 'ghana_card', id_number: '' });
  const idTypesWithNoBack = ['passport', 'student_id', 'nhis_card'];
  const showIdBack = !idTypesWithNoBack.includes(kycForm.id_type);
  const [kycFiles, setKycFiles] = useState({ id_front: null, selfie: null, id_back: null, proof_of_address: null, student_id: null, admission_letter: null, property_ownership_doc: null, business_registration: null });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);

  const fetchCities = async (region) => {
    try {
      const res = await ghanaAPI.getCities({ region });
      setCities(res.data.cities || []);
    } catch (_) {}
  };

  useEffect(() => {
    const loadGhanaData = async () => {
      try {
        const [regRes, uniRes] = await Promise.all([
          ghanaAPI.getRegions(),
          ghanaAPI.getUniversities(),
        ]);
        setRegions(regRes.data.regions || []);
        const uniNames = (uniRes.data.universities || []).map(u => `${u.name} (${u.abbreviation})`);
        setUniversities(uniNames);
      } catch (_) {}
      if (form.region) fetchCities(form.region);
    };
    loadGhanaData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true); setError('');
    try {
      const res = await authAPI.uploadAvatar(file);
      setUser({ ...user, avatar_url: res.data.avatar_url });
      setSuccess('Profile picture updated!');
    } catch (err) {
      setError(extractError(err));
    } finally { setAvatarUploading(false); }
  };

  React.useEffect(() => {
    const fetchKyc = async () => {
      try {
        const res = await kycAPI.getMyStatus();
        setKycStatus(res.data);
      } catch (_) {}
    };
    fetchKyc();
  }, []);

  const handleKycSubmit = async () => {
    setKycLoading(true); setError(''); setSuccess('');
    try {
      const formData = new FormData();
      formData.append('id_type', kycForm.id_type);
      formData.append('id_number', kycForm.id_number);
      if (kycFiles.id_front) formData.append('id_front', kycFiles.id_front);
      if (kycFiles.selfie) formData.append('selfie', kycFiles.selfie);
      if (kycFiles.id_back) formData.append('id_back', kycFiles.id_back);
      if (kycFiles.proof_of_address) formData.append('proof_of_address', kycFiles.proof_of_address);
      if (kycFiles.student_id) formData.append('student_id', kycFiles.student_id);
      if (kycFiles.admission_letter) formData.append('admission_letter', kycFiles.admission_letter);
      if (kycFiles.property_ownership_doc) formData.append('property_ownership_doc', kycFiles.property_ownership_doc);
      if (kycFiles.business_registration) formData.append('business_registration', kycFiles.business_registration);
      await kycAPI.submit(formData);
      setSuccess('KYC documents submitted successfully! Awaiting review.');
      const res = await kycAPI.getMyStatus();
      setKycStatus(res.data);
    } catch (err) {
      setError(extractError(err));
    } finally { setKycLoading(false); }
  };

  const handleUpdate = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await authAPI.updateMe(form);
      setUser(res.data);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(extractError(err));
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Passwords do not match'); return;
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      await authAPI.changePassword({ old_password: passwordForm.old_password, new_password: passwordForm.new_password });
      setSuccess('Password changed successfully');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(extractError(err));
    } finally { setLoading(false); }
  };

  const roleColors = { super_admin: 'error', landlord: 'primary', agent: 'secondary', tenant: 'success', rent_control: 'warning' };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>Profile</Typography>

      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={user?.avatar_url ? `${API_BASE}${user.avatar_url}` : undefined}
              sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}
            >
              {!user?.avatar_url && <>{user?.first_name?.[0]}{user?.last_name?.[0]}</>}
            </Avatar>
            <Button
              component="label"
              size="small"
              sx={{
                position: 'absolute', bottom: -4, right: -4, minWidth: 32, width: 32, height: 32,
                borderRadius: '50%', bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }, p: 0,
              }}
              disabled={avatarUploading}
            >
              <CameraAlt sx={{ fontSize: 16 }} />
              <input type="file" hidden accept="image/*" onChange={handleAvatarUpload} />
            </Button>
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>{user?.first_name} {user?.last_name}</Typography>
            <Typography color="text.secondary">{user?.email}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip label={user?.role?.replace('_', ' ')} size="small" color={roleColors[user?.role] || 'default'} sx={{ textTransform: 'capitalize' }} />
              {user?.is_verified ? (
                <Chip icon={<CheckCircle />} label="Verified" size="small" color="success" />
              ) : (
                <Chip icon={<Warning />} label="Unverified" size="small" color="warning" />
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Personal Info" />
        <Tab label="Change Password" />
        <Tab label="KYC Verification" />
      </Tabs>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {tab === 0 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={user?.email} disabled /></Grid>

            <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Personal Details</Typography></Divider></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} SelectProps={{ native: true }}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></Grid>

            {user?.role === 'tenant' && (
              <>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Educational Info</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo options={universities} value={form.university || ''}
                    onInputChange={(e, v) => setForm({ ...form, university: v })}
                    renderInput={(params) => <TextField {...params} fullWidth label="University / College" placeholder="Search institution..." />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Student ID" value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Course / Programme" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Year of Study" value={form.year_of_study} onChange={(e) => setForm({ ...form, year_of_study: e.target.value })} /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="e.g. 200" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Enrollment Year" value={form.enrollment_year} onChange={(e) => setForm({ ...form, enrollment_year: e.target.value })} placeholder="e.g. 2023" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Expected Graduation" value={form.expected_graduation} onChange={(e) => setForm({ ...form, expected_graduation: e.target.value })} placeholder="e.g. 2027" /></Grid>

                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Guardian / Next of Kin</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Guardian Full Name" value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Guardian Phone" value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Guardian Email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="Relationship" value={form.guardian_relationship} onChange={(e) => setForm({ ...form, guardian_relationship: e.target.value })} SelectProps={{ native: true }}>
                    <option value="">Select</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="spouse">Spouse</option>
                    <option value="uncle_aunt">Uncle/Aunt</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="other">Other</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}><TextField fullWidth label="Guardian Address" value={form.guardian_address} onChange={(e) => setForm({ ...form, guardian_address: e.target.value })} multiline rows={2} /></Grid>

                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Emergency Contact</Typography></Divider></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Emergency Contact Name" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Emergency Phone" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="Relationship" value={form.emergency_contact_relationship} onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })} SelectProps={{ native: true }}>
                    <option value="">Select</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="spouse">Spouse</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </TextField>
                </Grid>
              </>
            )}
            {(user?.role === 'landlord' || user?.role === 'agent') && (
              <>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Business Info</Typography></Divider></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Bio" multiline rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Grid>
              </>
            )}
            <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Address</Typography></Divider></Grid>
            <Grid item xs={12}><TextField fullWidth label="Address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={regions} value={form.region || ''} freeSolo
                onChange={(e, v) => { setForm({ ...form, region: v || '', city: '' }); if (v) fetchCities(v); }}
                onInputChange={(e, v) => setForm({ ...form, region: v })}
                renderInput={(params) => <TextField {...params} fullWidth label="Region" placeholder="Search region..." />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo options={cities} value={form.city || ''}
                onInputChange={(e, v) => setForm({ ...form, city: v })}
                renderInput={(params) => <TextField {...params} fullWidth label="City / Town" placeholder="Search city..." />}
              />
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Postal Code" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></Grid>
          </Grid>
          <Button variant="contained" startIcon={<Save />} onClick={handleUpdate} disabled={loading} sx={{ mt: 3 }}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 3, maxWidth: 500 }}>
          <Stack spacing={2}>
            <TextField fullWidth label="Current Password" type="password" value={passwordForm.old_password} onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })} />
            <TextField fullWidth label="New Password" type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
            <TextField fullWidth label="Confirm New Password" type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} />
            <Button variant="contained" onClick={handlePasswordChange} disabled={loading || !passwordForm.old_password || !passwordForm.new_password}>
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </Stack>
        </Paper>
      )}
      {tab === 2 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <VerifiedUser color={kycStatus?.status === 'approved' ? 'success' : 'action'} sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>KYC Verification</Typography>
              <Chip
                label={kycStatus?.status?.replace('_', ' ') || 'Not Submitted'}
                color={kycStatus?.status === 'approved' ? 'success' : kycStatus?.status === 'pending' ? 'warning' : kycStatus?.status === 'rejected' ? 'error' : 'default'}
                size="small" sx={{ textTransform: 'capitalize' }}
              />
            </Box>
          </Stack>

          {kycStatus?.status === 'rejected' && kycStatus?.rejection_reason && (
            <Alert severity="error" sx={{ mb: 2 }}>Rejected: {kycStatus.rejection_reason}</Alert>
          )}

          {kycStatus?.status === 'approved' ? (
            <Alert severity="success">Your identity has been verified. No further action needed.</Alert>
          ) : kycStatus?.status === 'pending' ? (
            <Alert severity="info">Your documents are being reviewed. This usually takes 1-2 business days.</Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="ID Type" value={kycForm.id_type}
                  onChange={(e) => setKycForm({ ...kycForm, id_type: e.target.value })}
                  SelectProps={{ native: true }}>
                  <option value="ghana_card">Ghana Card</option>
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="student_id">Student ID</option>
                  <option value="voters_card">Voter's Card</option>
                  <option value="nhis_card">NHIS Card</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ID Number" value={kycForm.id_number}
                  onChange={(e) => setKycForm({ ...kycForm, id_number: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                  {kycFiles.id_front ? kycFiles.id_front.name : 'ID Front (Required)'}
                  <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, id_front: e.target.files[0] })} />
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                  {kycFiles.selfie ? kycFiles.selfie.name : 'Selfie Photo (Required)'}
                  <input type="file" hidden accept="image/*" onChange={(e) => setKycFiles({ ...kycFiles, selfie: e.target.files[0] })} />
                </Button>
              </Grid>
              {showIdBack && (
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                    {kycFiles.id_back ? kycFiles.id_back.name : 'ID Back (Optional)'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, id_back: e.target.files[0] })} />
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                  {kycFiles.proof_of_address ? kycFiles.proof_of_address.name : 'Proof of Address (Optional)'}
                  <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, proof_of_address: e.target.files[0] })} />
                </Button>
              </Grid>
              {user?.role === 'tenant' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                      {kycFiles.student_id ? kycFiles.student_id.name : 'Student ID Card'}
                      <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, student_id: e.target.files[0] })} />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                      {kycFiles.admission_letter ? kycFiles.admission_letter.name : 'Admission Letter'}
                      <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, admission_letter: e.target.files[0] })} />
                    </Button>
                  </Grid>
                </>
              )}
              {(user?.role === 'landlord' || user?.role === 'agent') && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                      {kycFiles.property_ownership_doc ? kycFiles.property_ownership_doc.name : 'Property Ownership Doc'}
                      <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, property_ownership_doc: e.target.files[0] })} />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                      {kycFiles.business_registration ? kycFiles.business_registration.name : 'Business Registration'}
                      <input type="file" hidden accept="image/*,.pdf" onChange={(e) => setKycFiles({ ...kycFiles, business_registration: e.target.files[0] })} />
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleKycSubmit}
                  disabled={kycLoading || !kycForm.id_number || !kycFiles.id_front || !kycFiles.selfie}
                  startIcon={<Upload />}>
                  {kycLoading ? 'Submitting...' : 'Submit KYC Documents'}
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default ProfilePage;
